import type { AbilityDemo } from '@/features/playbook/types';

const demo: AbilityDemo = {
  id: 'lore',
  slug: 'lore-france',
  label: 'Area Lore (France)',
  roll: {
    formula: 'Int 2 + Area Lore (France) 3 = 5 + die',
    niche: "+1 when recalling geography of France — roads, valleys, settlements",
    altPairing: 'Per 1 + 3 = 4 when spotting something out-of-place in the landscape',
  },
  whenToUse: [
    'Recall which abbey controls a region, or which noble family holds grudges.',
    'Navigate back roads without a guide, reading the land itself.',
    'Know where scholars and libraries gather—your second home.',
    "Spot where something doesn't fit: a garrison, a stranger's dialect, a trade route shifted.",
  ],
  samples: [
    `GM: "Ex Astra covenant scouting through the Anjou valley in spring 1195. You search for rare plants that grow only in limestone soil and come upon a small monastery."
Elara: "Which abbey is this?"
You (Mikaela): "I recall which order controls it and whether they sympathize with hedge wizards. My travelling childhood taught me to read monastery politics into stone and crosses."
GM: "Roll Int 2 + Area Lore (France) 3 = 5 + die."

Teaches: Her travelling childhood (not Clerk) means she reads French networks through lived experience.`,
    `GM: "Spring 1195, Ex Astra scouting. Elara spots someone approaching on the road speaking rapid French. Something troubles her about his accent. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
Elara: "What region is he from?"
You (Mikaela): "I identify which region he comes from—whether he is local or a stranger from Normandy or beyond. Accent and phrasing map to nobility and trade routes."
GM: "Roll Int 2 + Area Lore (France) 3 = 5 + die."

Teaches: Her ear knows France through seasons of travel—accents teach feuds, routes, and merchant patterns.`,
    `GM: "Ex Astra spring 1195. The bridge ahead is barricaded. A toll-keeper demands payment. Sir Helgi wonders if you can find another route through the forest."
Sir Helgi: "Can you recall another way?"
You (Mikaela): "I remember which noble family holds the toll rights and which back road I studied years ago in merchant chronicles. I guide you around."
GM: "Roll Int 2 + Area Lore (France) 3 = 5 + die."

Teaches: Her knowledge of merchants' charters lets her navigate by scholarship, not just maps—score 3 gives her 5 total to find roads.`,
    `GM: "Ex Astra covenant, deep in the Vendée marshlands, spring 1195. The party loses daylight. Brother Alfric fears you will be caught outdoors after dark. Maps prove useless in the mist."
Brother Alfric: "Do you know a safe route?"
You (Mikaela): "I remember which houses feuded in years past and which routes remain safe. I guide us along an old causeway from a merchant chronicle I studied."
GM: "Roll Int 2 + Area Lore (France) 3 = 5 + die."

Teaches: Your clerk's memory for written words translates to real roads—knowledge of feuds keeps you safe.`,
    `GM: "Deep in the forest, Ex Astra's spring 1195 scouting party discovers an overgrown stone road running true as an arrow westward. Corvus suspects Roman work. Without it, you would wander for days searching for rare moonwort."
Corvus: "What is this road?"
You (Mikaela): "I recognize this as an old Via Romana from monastery archives, leading west toward Poitou where the ingredient grows. Ancient roads are geography in my well-read mind."
GM: "Roll Int 2 + Area Lore (France) 3 = 5 + die (niche Geography = 6). Roll Per 1 + Area Lore (France) 3 = 4 to spot oddities in the landscape."

Teaches: Per-1 alt for spotting—ancient roads hide in forests until careful eyes read them.`,
  ],
  vignette: "You've read enough accounts and copied enough charters to see France in your mind—not as maps, but as a web of relationships, roads, and roofs. A merchant's accent tells you his hometown; a description of hills and timber tells you which valley he means.",
  gotcha: 'You know about places; you still need courage or allies to act on it.',
};

export default demo;
